'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useScript } from 'usehooks-ts';
import { useSprdConsent } from '@/atoms/sprdConsentAtom';
import { Locale } from '@/utility/locale';

export interface ChatContextProps {
  isInitialized: boolean;
  isChatOpen: boolean;
  openChatDialog: () => void;
  closeChatDialog: () => void;
  toggleChatDialog: () => void;
  unreadMessageCount: number;
  callNow: (lineId: string) => void;
}

const DEFAULT_FUNCTION = (): void => console.error('No Context Available!');
// Above in-page nav (z-21), header (z-22), and promo banner (z-40); below drawer overlays (z-50).
const CHAT_Z_INDEX = 45;

type ConversationField = {
  id: string;
  value: string | number | boolean;
};

type MessengerFnc = {
  (type: 'messenger', action: 'show' | 'hide' | 'open' | 'close' | 'logoutUser'): void;
  (type: 'messenger', action: 'loginUser', callback: (login: (jwt: string) => void) => void): void;
  (type: 'messenger:on', event: 'open' | 'close', callback: () => void): void;
  (type: 'messenger:on', event: 'unreadMessages', callback: (count: number) => void): void;
  (type: 'messenger:set', key: 'locale', locale: Locale): void;
  (type: 'messenger:set', key: 'zIndex', zIndex: number): void;
  (type: 'messenger:set', key: 'cookies', isEnabled: boolean): void;
  (type: 'messenger:set', key: 'conversationFields', fields: ConversationField[]): void;
  (type: 'messenger:set', key: 'conversationTags', tags: string[]): void;
  (type: 'messenger:open', key: 'voice', line_id: string): void;
};

declare global {
  interface Window {
    zE?: MessengerFnc;
  }
}

export const ZendeskChatContext = createContext<ChatContextProps>({
  isInitialized: false,
  isChatOpen: false,
  openChatDialog: DEFAULT_FUNCTION,
  closeChatDialog: DEFAULT_FUNCTION,
  toggleChatDialog: DEFAULT_FUNCTION,
  unreadMessageCount: 0,
  callNow: DEFAULT_FUNCTION,
});

type ZendeskChatProviderProps = {
  children: ReactNode;
  scriptSrc?: string;
  locale: Locale;
  platform: Platform;
};

export const ZendeskChatProvider = ({
  children,
  scriptSrc,
  locale,
  platform,
}: ZendeskChatProviderProps) => {
  const consent = useSprdConsent();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const shouldLoadScript = !!consent?.functional && !!scriptSrc?.length;
  const scriptState = useScript(scriptSrc ?? '', {
    shouldPreventLoad: !shouldLoadScript,
    id: 'ze-snippet',
    removeOnUnmount: false,
  });

  const isChatAvailable =
    scriptState === 'ready' && window.zE !== undefined && !!consent?.functional;

  useEffect(() => {
    if (!isChatAvailable) {
      return;
    }

    window.zE?.('messenger:set', 'zIndex', CHAT_Z_INDEX);
    window.zE?.('messenger:on', 'open', () => setIsOpen(true));
    window.zE?.('messenger:on', 'close', () => setIsOpen(false));
    window.zE?.('messenger:on', 'unreadMessages', setUnreadCount);

    // Add customer locale
    // see https://support.zendesk.com/api/v2/locales/public.json
    window.zE?.('messenger:set', 'locale', locale);

    // Add platform
    window.zE?.('messenger:set', 'conversationTags', [`assign_${platform}`]);

    window.zE?.('messenger:set', 'conversationFields', [
      { id: '18207066243228', value: window.location.host },
    ]);
  }, [isChatAvailable, locale, platform]);

  const openChatDialog = useCallback(() => {
    if (!isChatAvailable) {
      console.error('window.zE is not available!');
      return;
    }

    window.zE?.('messenger', 'open');
  }, [isChatAvailable]);

  const callNow = useCallback(
    (lineId: string) => {
      if (!isChatAvailable) {
        console.error('window.zE is not available!');
        return;
      }

      window.zE?.('messenger:open', 'voice', lineId);
    },
    [isChatAvailable]
  );

  const closeChatDialog = useCallback(() => {
    if (!isChatAvailable) {
      console.error('window.zE is not available!');
      return;
    }

    window.zE?.('messenger', 'close');
  }, [isChatAvailable]);

  const toggleChatDialog = useCallback(() => {
    if (!isChatAvailable) {
      console.error('window.zE is not available!');
      return;
    }

    if (isOpen) {
      closeChatDialog();
    } else {
      openChatDialog();
    }
  }, [isChatAvailable, isOpen, openChatDialog, closeChatDialog]);

  const chatContext = useMemo<ChatContextProps>(
    () => ({
      isInitialized: isChatAvailable,
      isChatOpen: isOpen,
      openChatDialog,
      closeChatDialog,
      toggleChatDialog,
      unreadMessageCount: unreadCount,
      callNow,
    }),
    [
      callNow,
      closeChatDialog,
      isChatAvailable,
      isOpen,
      openChatDialog,
      toggleChatDialog,
      unreadCount,
    ]
  );

  return <ZendeskChatContext value={chatContext}>{children}</ZendeskChatContext>;
};

export const useZendeskChat = (): ChatContextProps =>
  useContext<ChatContextProps>(ZendeskChatContext);
